import React, { useEffect, useRef, useState } from "react";
import {
  DataGrid,
  Column,
  FilterRow,
  GroupPanel,
  Grouping,
  SearchPanel,
  DataGridTypes,
  Export,
  Pager,
  Paging,
  Selection,
  DataGridRef,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver-es";

import { Box } from "@mui/material"; // kalau mau pakai MUI icon/action
import { Icon } from "@iconify/react";

const pageSizes = [10, 25, 50, 100];

// detect tipe data kolom
const detectFieldType = (value: any) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string") {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDateRegex.test(value)) return "date";
  }
  return "string";
};

// render string berisi tag HTML
const renderTag = (html: string) => (
  <span dangerouslySetInnerHTML={{ __html: html }} />
);

interface CustomRender {
  key: string;
  renderCell: (cellData: any) => React.ReactNode;
}

interface ActionColumn {
  caption?: string;
  width?: number;
  renderCell: (row: any) => React.ReactNode;
}

type DynamicDataGridProps = {
  data: any[];
  title: string;
  hiddenColumns?: string[];
  onEditRow?: (rowData: any) => void;
  onDeleteRow?: (rowData: any) => void;
  handleSelection?: any;
  customRenderers?: Record<string, (value: any, rowData: any) => React.ReactNode>;
  customRender?: CustomRender[];
  rowClick?: (e: any) => void;
  cellClick?: (e: any) => void;
  actionsCaption?: string;
  hideEditButton?: boolean;
  hideDeleteButton?: boolean;
  actions?: ActionColumn[]; // ✅ props baru
};

const DynamicDataGrid: React.FC<DynamicDataGridProps> = ({
  data,
  title,
  hiddenColumns = [],
  onEditRow,
  onDeleteRow,
  handleSelection,
  customRenderers = {},
  customRender = [],
  rowClick,
  cellClick,
  actionsCaption = "ACTIONS",
  hideEditButton = false,
  hideDeleteButton = false,
  actions = [], // ✅ default kosong
}) => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const dataGrid = useRef<DataGridRef>(null);

  useEffect(() => {
    if (Array.isArray(data)) {
      setDataSource(data);
    }
  }, [data]);

  const handleDelete = (rowData: any) => {
    if (onDeleteRow) {
      onDeleteRow(rowData);
    } else {
      const filtered = dataSource.filter((item) => item.id !== rowData.id);
      setDataSource(filtered);
    }
  };

  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: any) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          `${title}.xlsx`
        );
      });
    });
  };

  // cek apakah tombol visible
  const showEdit = onEditRow && !hideEditButton;
  const showDelete = onDeleteRow && !hideDeleteButton;

  return (
    <DataGrid
      ref={dataGrid}
      dataSource={dataSource}
      keyExpr="id"
      showBorders
      columnAutoWidth
      allowColumnReordering
      repaintChangesOnly
      rowAlternationEnabled={false}
      onSelectionChanged={handleSelection}
      onExporting={onExporting}
      onEditingStart={(e) => {
        e.cancel = true;
        if (onEditRow) onEditRow(e.data);
      }}
      onRowClick={rowClick}
      onCellClick={cellClick}
    >
      <FilterRow visible={false} />
      <GroupPanel visible={true} />
      <Grouping autoExpandAll={true} />
      <SearchPanel visible={true} highlightCaseSensitive={true} />
      <Pager
        visible={true}
        allowedPageSizes={pageSizes}
        showPageSizeSelector={true}
      />
      <Paging defaultPageSize={10} />
      <Export key="id" enabled={true} allowExportSelectedData={true} />

      {/* Kolom dinamis */}
      {dataSource.length > 0 &&
        typeof dataSource[0] === "object" &&
        !Array.isArray(dataSource[0]) &&
        Object.keys(dataSource[0])
          .filter(
            (field) =>
              !field.toLowerCase().includes("_hidden") &&
              !hiddenColumns.includes(field)
          )
          .map((field) => {
            const value = dataSource[0][field];
            const dataType = detectFieldType(value);

            const custom = customRender.find((c) => c.key === field);

            return (
              <Column
                key={field}
                dataField={field}
                dataType={dataType}
                caption={field.replace(/_/g, " ").toUpperCase()}
                cellRender={({ value, data }) => {
                  if (custom) {
                    return custom.renderCell({ value, data });
                  }
                  if (customRenderers[field]) {
                    return customRenderers[field](value, data);
                  }
                  if (typeof value === "string" && value.includes("<")) {
                    return renderTag(value);
                  }
                  if (value instanceof Date)
                    return <span>{value.toLocaleDateString()}</span>;
                  return <span>{String(value ?? "")}</span>;
                }}
              />
            );
          })}

      {/* Kolom bawaan Edit/Delete */}
      {(onEditRow || onDeleteRow) && (
        <Column
          dataField="actions"
          caption={actionsCaption ?? "ACTIONS"}
          fixed
          fixedPosition="left"
          allowSorting={false}
          allowFiltering={false}
          cellRender={({ data }) => {
            if (!showEdit && !showDelete) {
              return <div />;
            }

            return (
              <div style={{ display: "flex", gap: 4 }}>
                {showEdit && (
                  <Button
                    icon="edit"
                    stylingMode="text"
                    hint="Edit"
                    onClick={() => onEditRow?.(data)}
                  />
                )}
                {showDelete && (
                  <Button
                    icon="trash"
                    stylingMode="text"
                    hint="Hapus"
                    onClick={() => handleDelete(data)}
                  />
                )}
              </div>
            );
          }}
        />
      )}

      {/* Kolom Actions custom */}
      {actions.map((action, idx) => (
        <Column
          key={`custom-action-${idx}`}
          caption={action.caption ?? "Aksi"}
          width={action.width ?? 100}
          fixed
          fixedPosition="left"
          allowSorting={false}
          allowFiltering={false}
          cellRender={({ data }) => action.renderCell(data)}
        />
      ))}

      <Selection
        mode="multiple"
        selectAllMode={"allPages"}
        showCheckBoxesMode={"always"}
      />
    </DataGrid>
  );
};

export default DynamicDataGrid;
